import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { FaBuilding, FaPlus, FaCheckCircle, FaTimesCircle, FaGlobe, FaPalette } from 'react-icons/fa';

interface Chapter {
  id: number;
  name: string;
  code: string;
  logo?: string;
  primary_color?: string;
  domain_url?: string;
  status: 'active' | 'inactive';
  created_at?: string;
}

const ChapterManagement: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state for creating chapter
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    primary_color: '#0A2451',
    domain_url: '',
    logo: ''
  });

  const fetchChapters = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://rmbbackend.rmbjaipur.com/auth/chapters');
      if (response.data.success) {
        setChapters(response.data.chapters || []);
      }
    } catch {
      setError('Failed to load chapters list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChapters();
  }, []);

  const handleCreateChapter = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || !formData.code) {
      setError('Chapter Name and Unique Code are required.');
      return;
    }

    try {
      const res = await axios.post('https://rmbbackend.rmbjaipur.com/auth/chapters', formData);
      if (res.data.success) {
        setSuccess(res.data.message || 'Chapter created successfully!');
        setShowCreateModal(false);
        setFormData({ name: '', code: '', primary_color: '#0A2451', domain_url: '', logo: '' });
        fetchChapters();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create chapter');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await axios.delete(`https://rmbbackend.rmbjaipur.com/auth/chapters/${id}`);
      fetchChapters();
    } catch {
      setError('Failed to update status');
    }
  };

  return (
    <div className={`p-6 ${isLight ? 'bg-slate-50 text-slate-900' : 'bg-[#060D1A] text-slate-100'} rounded-2xl shadow-xl min-h-[600px]`}>
      
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-slate-200/20">
        <div>
          <h2 className="text-2xl font-black flex items-center gap-2">
            <FaBuilding className="text-[#FDBC12]" /> Multi-Chapter Whitelabel Management
          </h2>
          <p className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'} mt-1`}>
            Manage independent chapter portals, whitelabel domains, and regional admin configurations.
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-bold text-xs rounded-xl shadow-lg transition-all"
        >
          <FaPlus /> Add New Chapter
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {success && (
        <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs rounded-xl flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}

      {/* Chapter Cards Grid */}
      {loading ? (
        <div className="py-20 text-center text-slate-400 text-sm">Loading chapters...</div>
      ) : chapters.length === 0 ? (
        <div className="py-20 text-center text-slate-400 text-sm">No chapters found. Add one above!</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {chapters.map(c => (
            <div
              key={c.id}
              className={`p-5 rounded-2xl border ${isLight ? 'bg-white border-slate-200 shadow-sm' : 'bg-[#0b172a] border-slate-800'} transition-all hover:border-[#FDBC12]/50`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-lg shadow-md"
                    style={{ backgroundColor: c.primary_color || '#0A2451' }}
                  >
                    {c.name.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-base">{c.name}</h3>
                    <span className="text-[10px] uppercase font-mono tracking-wider px-2 py-0.5 rounded bg-slate-500/10 text-amber-500">
                      code: {c.code}
                    </span>
                  </div>
                </div>

                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${c.status === 'active' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-slate-500/10 border-slate-500/30 text-slate-400'}`}>
                  {c.status === 'active' ? <FaCheckCircle /> : <FaTimesCircle />}
                  {c.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-2 text-xs py-3 border-t border-b border-slate-500/10 my-3">
                <div className="flex justify-between items-center text-slate-400">
                  <span className="flex items-center gap-1.5"><FaGlobe /> Whitelabel Domain:</span>
                  <span className="font-mono text-slate-200">{c.domain_url || `${c.code}.rmb.org`}</span>
                </div>
                <div className="flex justify-between items-center text-slate-400">
                  <span className="flex items-center gap-1.5"><FaPalette /> Theme Color:</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3.5 h-3.5 rounded-full border border-white/20" style={{ backgroundColor: c.primary_color || '#0A2451' }}></span>
                    <span className="font-mono">{c.primary_color || '#0A2451'}</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-1">
                <span className="text-[10px] text-slate-500">ID: #{c.id}</span>
                <button
                  onClick={() => handleToggleStatus(c.id)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all ${c.status === 'active' ? 'border-rose-500/30 text-rose-400 hover:bg-rose-500/10' : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'}`}
                >
                  {c.status === 'active' ? 'Deactivate Chapter' : 'Activate Chapter'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal: Create Chapter */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`w-full max-w-md p-6 rounded-2xl border ${isLight ? 'bg-white text-slate-900 border-slate-200' : 'bg-[#0b172a] text-slate-100 border-slate-800'} shadow-2xl`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Add New Chapter Portal</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white text-lg">&times;</button>
            </div>

            <form onSubmit={handleCreateChapter} className="space-y-4 text-xs">
              <div>
                <label className="block mb-1 font-semibold text-slate-300">Chapter Name *</label>
                <input
                  type="text"
                  placeholder="e.g. RMB Mumbai"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 focus:outline-none focus:border-amber-500"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 font-semibold text-slate-300">Chapter Code / Slug *</label>
                <input
                  type="text"
                  placeholder="e.g. mumbai"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 focus:outline-none focus:border-amber-500 font-mono"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 font-semibold text-slate-300">Primary Theme Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-10 h-10 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={e => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-1 font-semibold text-slate-300">Whitelabel Custom Domain (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. rmbmumbai.org"
                  value={formData.domain_url}
                  onChange={e => setFormData({ ...formData, domain_url: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-500/10 border border-slate-500/20 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-500/20 text-slate-400 hover:bg-slate-500/10 font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl shadow-md"
                >
                  Create Chapter
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChapterManagement;
